/**
 * 账号开通数量统计页面
 * 展示每日开通统计、趋势图表、数据导出
 * 统计维度：账号总数、Token总量、VIP总天数
 */

import { useState, useEffect } from 'react';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import {
  Users,
  Coins,
  Calendar,
  TrendingUp,
  Download,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import {
  accountActivationStatsApi,
  type StatSummary,
  type StatsSummaryResponse,
  type StatsResponse,
} from '@/services/accountActivationStatsApi';

export function AccountActivationStatsPage() {
  // 汇总数据
  const [summary, setSummary] = useState<StatsSummaryResponse | null>(null);

  // 日期范围
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // 列表数据
  const [statsData, setStatsData] = useState<StatsResponse | null>(null);

  // 加载状态
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // 加载汇总数据
  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const data = await accountActivationStatsApi.getStatsSummary();
      setSummary(data);
    } catch (error: any) {
      toast.error('加载统计汇总失败', { description: error.message });
    } finally {
      setLoadingSummary(false);
    }
  };

  // 加载列表数据
  const loadStatsData = async () => {
    setLoading(true);
    try {
      const data = await accountActivationStatsApi.getActivationStats({
        start_date: startDate,
        end_date: endDate,
      });
      setStatsData(data);
    } catch (error: any) {
      toast.error('加载统计数据失败', { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  // 导出Excel
  const handleExport = async () => {
    try {
      await accountActivationStatsApi.exportStatsExcel({
        start_date: startDate,
        end_date: endDate,
      });
      toast.success('导出成功');
    } catch (error: any) {
      toast.error('导出失败', { description: error.message });
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    loadStatsData();
  }, [startDate, endDate]);

  // 格式化数字
  const formatNumber = (num: number) => {
    return num.toLocaleString('zh-CN');
  };

  // 获取趋势数据
  const getTrendData = () => {
    if (!summary?.trend) return [];
    return summary.trend.map((item) => ({
      date: item.stat_date.slice(5), // MM-DD
      accounts: item.total_accounts,
      tokens: item.total_tokens,
      success: item.success_count,
    }));
  };

  // 渲染统计卡片
  const renderStatCard = (
    title: string,
    stat: StatSummary,
    icon: React.ReactNode,
    colorClass: string,
    periodLabel?: string
  ) => {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
            {periodLabel && <span className="ml-2 text-xs">({periodLabel})</span>}
          </CardTitle>
          <div className={`p-2 rounded-lg ${colorClass}`}>{icon}</div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-bold">{formatNumber(stat.total_accounts)}</div>
            <div className="text-xs text-muted-foreground">账号</div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Coins className="w-3 h-3 text-amber-500" />
              <span>{formatNumber(stat.total_tokens)}</span>
              <span className="text-xs text-muted-foreground ml-1">Token</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-blue-500" />
              <span>{formatNumber(stat.total_vip_days)}</span>
              <span className="text-xs text-muted-foreground ml-1">VIP天数</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <LayoutWithSidebar title="数量统计">
      <div className="container mx-auto py-4 px-3 sm:py-8 sm:px-4 max-w-7xl">
        {/* 标题区域 */}
        <div className="mb-4 sm:mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">账号开通数量统计</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              统计账号开通数量、Token发放量及VIP天数（按量统计）
            </p>
          </div>
          <Button variant="outline" onClick={() => { loadSummary(); loadStatsData(); }}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>

        {/* 统计卡片区 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {loadingSummary ? (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="pb-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : summary ? (
            <>
              {renderStatCard(
                '今日开通',
                summary.today,
                <Users className="w-4 h-4 text-blue-600" />,
                'bg-blue-100 dark:bg-blue-900/30',
                '今日'
              )}
              {renderStatCard(
                '本月累计',
                summary.monthly,
                <Calendar className="w-4 h-4 text-purple-600" />,
                'bg-purple-100 dark:bg-purple-900/30',
                '本月'
              )}
              {renderStatCard(
                '本年累计',
                summary.yearly,
                <TrendingUp className="w-4 h-4 text-green-600" />,
                'bg-green-100 dark:bg-green-900/30',
                '本年'
              )}
            </>
          ) : null}
        </div>

        <Tabs defaultValue="trend" className="space-y-4">
          <TabsList>
            <TabsTrigger value="trend">
              <TrendingUp className="w-4 h-4 mr-2" />
              趋势分析
            </TabsTrigger>
            <TabsTrigger value="detail">
              <Users className="w-4 h-4 mr-2" />
              数据明细
            </TabsTrigger>
          </TabsList>

          {/* 趋势分析标签页 */}
          <TabsContent value="trend" className="space-y-4">
            {/* 趋势图表 */}
            <Card>
              <CardHeader>
                <CardTitle>每日开通趋势（近7天）</CardTitle>
                <CardDescription>账号开通数量变化</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getTrendData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--background)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                        }}
                        labelStyle={{ color: 'var(--foreground)' }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="accounts"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ fill: '#3b82f6' }}
                        name="开通账号数"
                      />
                      <Line
                        type="monotone"
                        dataKey="tokens"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={{ fill: '#f59e0b' }}
                        name="Token量"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Token与VIP统计对比 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Token与VIP统计</CardTitle>
                  <CardDescription>本月发放量（按量统计）</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {summary?.monthly && (
                    <>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Token发放量</span>
                          <span className="font-semibold">{formatNumber(summary.monthly.total_tokens)}</span>
                        </div>
                        <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 rounded-full"
                            style={{
                              width: `${Math.min((summary.monthly.total_tokens / 100000) * 100, 100)}%`,
                            }}
                          ></div>
                        </div>
                        <div className="text-xs text-muted-foreground text-right">
                          Token 按量统计
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>VIP天数</span>
                          <span className="font-semibold">{formatNumber(summary.monthly.total_vip_days)}天</span>
                        </div>
                        <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{
                              width: `${Math.min((summary.monthly.total_vip_days / 10000) * 100, 100)}%`,
                            }}
                          ></div>
                        </div>
                        <div className="text-xs text-muted-foreground text-right">
                          VIP 按个数统计
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* 各指标柱状图 */}
              <Card>
                <CardHeader>
                  <CardTitle>各指标占比（本月）</CardTitle>
                  <CardDescription>账号数、Token量、VIP天数对比</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          {
                            name: '账号数',
                            value: summary?.monthly?.total_accounts || 0,
                          },
                          {
                            name: 'Token量',
                            value: Math.round((summary?.monthly?.total_tokens || 0) / 1000),
                          },
                          {
                            name: 'VIP天数',
                            value: summary?.monthly?.total_vip_days || 0,
                          },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'var(--background)',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    注：Token量已转换为千为单位展示
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* 数据明细标签页 */}
          <TabsContent value="detail" className="space-y-4">
            {/* 日期筛选 */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row gap-4 items-end">
                  <div className="flex-1">
                    <Label htmlFor="startDate">开始日期</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="endDate">结束日期</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 30);
                      setStartDate(d.toISOString().split('T')[0]);
                      setEndDate(new Date().toISOString().split('T')[0]);
                    }}>
                      近30天
                    </Button>
                    <Button variant="outline" onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 7);
                      setStartDate(d.toISOString().split('T')[0]);
                      setEndDate(new Date().toISOString().split('T')[0]);
                    }}>
                      近7天
                    </Button>
                    <Button onClick={handleExport}>
                      <Download className="w-4 h-4 mr-2" />
                      导出
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 汇总信息 */}
            {statsData?.summary && (
              <Card className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{formatNumber(statsData.summary.total_accounts)}</div>
                      <div className="text-sm text-muted-foreground">总账号数</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{formatNumber(statsData.summary.total_tokens)}</div>
                      <div className="text-sm text-muted-foreground">总Token量</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{formatNumber(statsData.summary.total_vip_days)}</div>
                      <div className="text-sm text-muted-foreground">总VIP天数</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 数据表格 */}
            <Card>
              <CardHeader>
                <CardTitle>每日明细</CardTitle>
                <CardDescription>按日期统计的开通数据</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>日期</TableHead>
                        <TableHead className="text-right">开通账号</TableHead>
                        <TableHead className="text-right">Token额度</TableHead>
                        <TableHead className="text-right">VIP天数</TableHead>
                        <TableHead className="text-right">成功数</TableHead>
                        <TableHead className="text-right">失败数</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                          </TableCell>
                        </TableRow>
                      ) : statsData?.items && statsData.items.length > 0 ? (
                        statsData.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.stat_date}</TableCell>
                            <TableCell className="text-right">{formatNumber(item.total_accounts)}</TableCell>
                            <TableCell className="text-right">{formatNumber(item.total_tokens)}</TableCell>
                            <TableCell className="text-right">{formatNumber(item.total_vip_days)}</TableCell>
                            <TableCell className="text-right text-green-600">{formatNumber(item.success_count)}</TableCell>
                            <TableCell className="text-right text-red-500">{formatNumber(item.failed_count)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            暂无数据
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* 统计说明 */}
        <Card className="mt-6 bg-gray-50 dark:bg-gray-800/50">
          <CardContent className="pt-4">
            <h3 className="font-semibold mb-2">统计说明</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                <span>账号总数：开通的账号总数</span>
              </div>
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-amber-500" />
                <span>Token总量：发放的Token额度总和</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-500" />
                <span>VIP总天数：VIP天数总和</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </LayoutWithSidebar>
  );
}
